/**
 * A view's data (docs/views.md#2-data): collections of records, each a row of JSON fields with the server's own beside
 * them, kept by the view object in partitions, one per chat the view is in, one for the view, and one per person, so a
 * viewer syncs only the partitions they may read. A write is an operation on one record: a new record's fields, a patch
 * merged field by field, or a removal. The server applies each in the order it arrives, so two people changing different
 * fields of a record both keep their change, and `$add`, `$remove`, and `$inc` change lists and counts by what is there
 * when they land. One implementation applies them everywhere: on the server, on a device showing its own write before
 * the server answers, and in the view's frame. This module has no host dependencies.
 */

export type Json = null | boolean | number | string | Json[] | { [key: string]: Json };
export type Fields = Record<string, Json>;
/** A record as the page API hands it out: its fields with the server's beside them. */
export interface Rec {
  id: string;
  n: number | null;
  createdAt: string | null;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  [field: string]: Json | undefined;
}
/**
 * Another view's collection as a view that names it in `uses` reads it (docs/views.md#2-data): its records, the section of
 * that view that shows one, and the start of that view's links, so a record opens there; null in a link view, which has no links.
 */
export interface UsedCollection {
  records: (Rec & { chat: string | null })[];
  section: string | null;
  link: string | null;
}
/** A change to one field: a value, null to delete it, or an operation on what is there. */
export type FieldChange = Json | { $add: Json | Json[] } | { $remove: Json | Json[] } | { $inc: number };
export type Patch = Record<string, FieldChange>;
/** A write to one record, as a device, an agent, a seed, an integration, or a runner sends it. */
export type Op =
  | { op: "insert"; collection: string; id: string; fields: Fields }
  | { op: "update"; collection: string; id: string; patch: Patch }
  | { op: "put"; collection: string; id: string; fields: Fields }
  | { op: "remove"; collection: string; id: string };

export const DATA_LIMITS = {
  recordBytes: 65_536,
  fields: 128,
  /** Records a partition holds at most. */
  records: 5_000,
  listItems: 1_000,
} as const;
export const COLLECTION_NAME = /^[a-z][a-z0-9_-]{0,39}$/;
export const RECORD_ID = /^[A-Za-z0-9_.:-]{1,80}$/;
/** Whether `value` is a string `pattern` accepts: a pattern alone would take `undefined` for the word "undefined". */
export const named = (value: unknown, pattern: RegExp): value is string =>
  typeof value === "string" && pattern.test(value);
const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
/** Names a record's own fields cannot take: the page API hands these out beside them. */
export const RESERVED_FIELDS = new Set([
  "id",
  "n",
  "chat",
  "createdAt",
  "createdBy",
  "updatedAt",
  "updatedBy",
  "pending",
]);
/** The fields the server sets, which no write names. */
export const SERVER_FIELDS = ["n", "createdAt", "createdBy", "updatedAt", "updatedBy"] as const;

export function validField(name: string): boolean {
  return FIELD_NAME.test(name) && !RESERVED_FIELDS.has(name);
}
/** A merging change (`$add`, `$remove`, or `$inc`) as its name and operand; null for a plain value. */
const opOf = (value: unknown): [op: string, operand: unknown] | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const entries = Object.entries(value);
  const first = entries[0];
  return entries.length === 1 && first && ["$add", "$remove", "$inc"].includes(first[0]) ? first : null;
};
export const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const plain = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

function checkJson(value: unknown, where: string): void {
  if (value === undefined || typeof value === "function" || typeof value === "symbol" || typeof value === "bigint")
    throw new Error(`${where} must be JSON.`);
  if (typeof value === "number" && !Number.isFinite(value)) throw new Error(`${where} must be a finite number.`);
}
/** A new record's fields: names the page API doesn't use, holding JSON. */
export function checkFields(fields: Fields): void {
  if (!fields || typeof fields !== "object" || Array.isArray(fields)) throw new Error("Fields are an object.");
  for (const [field, value] of Object.entries(fields)) {
    if (!validField(field)) throw new Error(`Invalid field name: ${field}.`);
    if (value !== null) checkJson(value, field);
  }
}
/** A patch: each field a value, null, or one merging change with a sound operand. */
export function checkPatch(patch: Patch): void {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) throw new Error("A patch is an object.");
  for (const [field, change] of Object.entries(patch)) {
    if (!validField(field)) throw new Error(`Invalid field name: ${field}.`);
    if (change === null) continue;
    const merging = opOf(change);
    if (merging) {
      const [op, operand] = merging;
      if (op === "$inc") {
        if (typeof operand !== "number" || !Number.isFinite(operand))
          throw new Error(`$inc on ${field} takes a number.`);
      } else for (const item of (Array.isArray(operand) ? operand : [operand]) as Json[]) checkJson(item, field);
    } else checkJson(change, field);
  }
}
/** A record within its limits: so many fields, so long a list, so many bytes. */
export function checkRecord(rec: Rec): void {
  const fields = userFields(rec);
  if (Object.keys(fields).length > DATA_LIMITS.fields) throw new Error(`Record ${rec.id} has too many fields.`);
  for (const [field, value] of Object.entries(fields))
    if (Array.isArray(value) && value.length > DATA_LIMITS.listItems)
      throw new Error(`The list ${field} of record ${rec.id} is too long.`);
  if (JSON.stringify(rec).length > DATA_LIMITS.recordBytes) throw new Error(`Record ${rec.id} exceeds the size limit.`);
}

/** The fields a record's authors wrote: without the server's, and without the chat a reader may have put beside them. */
export function userFields(rec: Rec | null): Fields {
  const out: Fields = {};
  if (rec)
    for (const [key, value] of Object.entries(rec))
      if (!RESERVED_FIELDS.has(key) && value !== undefined) out[key] = value as Json;
  return out;
}

/**
 * A patch merged into fields, the one way everywhere: null deletes a field, `$inc` adds to a number (from zero when the
 * field holds none), `$add` puts items on a list that aren't on it and `$remove` takes them off (a plain value, or
 * nothing yet, becomes a list holding what was there), and anything else sets the field.
 */
export function applyPatch(fields: Fields, patch: Patch): Fields {
  const next: Fields = { ...fields };
  for (const [field, change] of Object.entries(patch)) {
    if (change === null) {
      delete next[field];
      continue;
    }
    const merging = opOf(change);
    if (merging) {
      const [op, operand] = merging;
      if (op === "$inc") {
        next[field] = (typeof next[field] === "number" ? (next[field] as number) : 0) + (operand as number);
        continue;
      }
      const items = (Array.isArray(operand) ? operand : [operand]) as Json[];
      const existing = next[field];
      const list = Array.isArray(existing) ? [...existing] : existing === undefined ? [] : [existing];
      if (op === "$add") {
        for (const item of items) if (!list.some((x) => same(x, item))) list.push(plain(item));
        next[field] = list;
      } else next[field] = list.filter((x) => !items.some((item) => same(item, x)));
      continue;
    }
    next[field] = plain(change as Json);
  }
  return next;
}

/** A record with no server fields yet: what an insert starts from. */
export const blankRecord = (id: string): Rec => ({
  id,
  n: null,
  createdAt: null,
  createdBy: null,
  updatedAt: null,
  updatedBy: null,
});
const withFields = (rec: Rec, fields: Fields): Rec => ({
  id: rec.id,
  n: rec.n,
  createdAt: rec.createdAt,
  createdBy: rec.createdBy,
  updatedAt: rec.updatedAt,
  updatedBy: rec.updatedBy,
  ...fields,
});

/**
 * An operation applied to a record as it is, before the server's stamps: a new record from its fields, a record patched,
 * or none once removed. It is what the server saves once it has stamped and numbered it, and what a device shows while
 * it waits. One that can't apply throws: a record that exists already, none to change or remove, or fields the page API
 * doesn't allow.
 */
export function applyOp(before: Rec | null, op: Op): { kind: "insert" | "update" | "remove"; after: Rec | null } {
  if (!op || typeof op !== "object") throw new Error("Unknown operation.");
  if (!named(op.collection, COLLECTION_NAME)) throw new Error("Invalid collection name.");
  if (!named(op.id, RECORD_ID)) throw new Error("Invalid record id.");
  const kind = op.op === "put" ? (before ? "update" : "insert") : op.op;
  if (kind === "insert") {
    const fields = (op as { fields?: Fields }).fields ?? {};
    checkFields(fields);
    if (before) throw new Error(`A record ${op.id} already exists in ${op.collection}.`);
    const own: Fields = {};
    for (const [field, value] of Object.entries(fields)) if (value !== null) own[field] = plain(value);
    return { kind, after: withFields(blankRecord(op.id), own) };
  }
  if (!before) throw new Error(`No record ${op.id} in ${op.collection}.`);
  if (kind === "remove") return { kind, after: null };
  if (kind !== "update") throw new Error("Unknown operation.");
  if (op.op === "put") {
    // A put names all of the record's fields, so those it leaves out go, as they would from a record it inserted.
    const fields = (op as { fields?: Fields }).fields ?? {};
    checkFields(fields);
    const own: Fields = {};
    for (const [field, value] of Object.entries(fields)) if (value !== null) own[field] = plain(value);
    return { kind, after: withFields(before, own) };
  }
  const patch = (op as { patch?: Patch }).patch ?? {};
  checkPatch(patch);
  return { kind, after: withFields(before, applyPatch(userFields(before), patch)) };
}

/** A record's change: what it was and is, and each field that differs, for history and the write rules. */
export interface RecordChange {
  collection: string;
  id: string;
  kind: "insert" | "update" | "remove";
  before: Rec | null;
  after: Rec | null;
  /** Each changed field, before and after; the server's own fields are left out. */
  fields: Record<string, [Json, Json]>;
}
/** The fields that differ between two readings of a record, the server's own left out. */
export function fieldChanges(before: Rec | null, after: Rec | null): Record<string, [Json, Json]> {
  const x = userFields(before),
    y = userFields(after),
    fields: Record<string, [Json, Json]> = {};
  for (const key of new Set([...Object.keys(x), ...Object.keys(y)]))
    if (!same(x[key], y[key])) fields[key] = [x[key] ?? null, y[key] ?? null];
  return fields;
}
